import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthCard } from '@/components/portal/auth-card'
import { Wordmark } from '@/components/portal/wordmark'
import { acceptInvite, getInvitePreview } from '@/lib/auth'
import { postAuthPath } from '@/hooks/use-auth'
import { useQueryClient } from '@tanstack/react-query'

export function AcceptInvitePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('+254')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadError('This invitation link is invalid.')
      setLoadingPreview(false)
      return
    }

    getInvitePreview(token)
      .then(({ data }) => {
        setEmail(data.email)
        setName(data.name)
        setPhone(data.phone || '+254')
        setExpiresAt(data.expiresAt)
      })
      .catch(() => {
        setLoadError(
          'This invitation is invalid or has expired. Contact the estate office for a new link.',
        )
      })
      .finally(() => setLoadingPreview(false))
  }, [token])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match')
      return
    }

    setSubmitting(true)
    try {
      const { user } = await acceptInvite(token, name, phone, password, confirmPassword)
      // New session: drop anything cached for a previous user on this browser
      queryClient.clear()
      navigate(postAuthPath(user), { replace: true })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not activate account')
    } finally {
      setSubmitting(false)
    }
  }

  const expiryLabel =
    expiresAt != null
      ? new Date(expiresAt * 1000).toLocaleString('en-KE', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
        })
      : null

  return (
    <AuthCard>
      <Wordmark portal="Estate Portal" className="mb-8" />

      <h2 className="mb-2 text-center font-serif text-2xl font-medium text-ink">
        Set up your account
      </h2>
      <p className="mb-6 text-center font-sans text-sm text-muted-foreground">
        Welcome to Riverpark Estate Portal. Confirm your details and create a password.
      </p>

      {loadingPreview && (
        <p className="text-center font-sans text-sm text-muted-foreground">Verifying invitation…</p>
      )}

      {loadError && (
        <div className="space-y-4 text-center">
          <p className="font-sans text-sm text-destructive" role="alert">
            {loadError}
          </p>
          <Link to="/login" className="font-sans text-sm text-primary hover:text-green-lt">
            Back to sign in
          </Link>
        </div>
      )}

      {!loadingPreview && !loadError && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {email && <Input label="Email address" type="email" value={email} readOnly disabled />}
          <Input
            label="Full name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            required
          />
          <Input
            label="Phone number"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            hint="Format: +254XXXXXXXXX"
            autoComplete="tel"
            required
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            hint="At least 8 characters with uppercase, lowercase and a number"
            required
          />
          <Input
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />

          {expiryLabel && (
            <p className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-3 py-2 font-sans text-xs text-status-pending-text">
              This invitation expires on {expiryLabel} (48 hours from when it was sent).
            </p>
          )}

          {submitError && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {submitError}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" loading={submitting}>
            Activate account
          </Button>
        </form>
      )}
    </AuthCard>
  )
}
